import { useEffect, useState } from "react";

/**
 * Shared countdown helpers for the handover-code flow. Both the outgoing
 * doctor's code-display screen (E6.4) and the incoming doctor's lockout error
 * (E6.5) tick down to a server-supplied wall-clock instant — the server is the
 * authority on expiry, the countdown is purely cosmetic. Sharing the format and
 * tick logic keeps the two screens in sync (same cadence, same `1h 02m 03s`
 * shape) and avoids one drifting away from the other.
 */

/**
 * Format milliseconds as `1h 02m 03s`. The minutes and seconds are zero-padded
 * so the digits don't jitter as they tick; hours are left bare because the
 * display is dominated by minutes for the 10-minute lockout and the 2-hour code
 * lifetime alike. Negative inputs are clamped to zero.
 */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
}

/**
 * Tick down to `target` (ISO string, `Date`, or epoch-ms) on a 1s interval.
 * Returns the remaining ms (clamped at 0 in {@link formatRemaining}, not here —
 * callers often need the raw signed value to swap content at expiry). The
 * effect re-runs only when `target` changes, so passing the same instant across
 * renders is safe and won't leak intervals.
 */
export function useCountdown(target: string | Date | number): number {
  const targetMs =
    typeof target === "number"
      ? target
      : typeof target === "string"
        ? Date.parse(target)
        : target.getTime();
  const [remainingMs, setRemainingMs] = useState(() => targetMs - Date.now());

  useEffect(() => {
    const tick = (): void => {
      setRemainingMs(targetMs - Date.now());
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [targetMs]);

  return remainingMs;
}
